import { render } from './apiUtils';
import { getDataAll } from './dataUtils';

/**
 * Включает/выключает элемент (обычно кнопку) с возможностью отображения загрузки
 * 
 * @param {Object} options - Параметры
 * @param {HTMLElement} options.elem - Элемент для управления
 * @param {boolean} [options.disable=false] - Отключить элемент (true) или включить (false)
 * @param {string|null} [options.loading=null] - Текст загрузки или null для восстановления
 * @returns {Promise} - Промис для цепочки вызовов
 */
export function elemEnable({ elem, disable = false, loading = 'Обработка...' }) {
  if (!elem) {
    console.warn('elemEnable: elem не указан');
    return Promise.resolve();
  }

  // Сохраняем оригинальный контент при первом вызове
  if (!elem.dataset.originalContent) {
    elem.dataset.originalContent = elem.innerHTML;
  }

  // Устанавливаем состояние disabled
  elem.disabled = disable;

  // Если указан текст загрузки - показываем спиннер
  if (loading !== null && disable) {
    elem.innerHTML = `<span class="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></span><span> ${loading}</span>`;
  }
  // Если включаем элемент обратно - восстанавливаем оригинальный контент
  else if (!disable && elem.dataset.originalContent) {
    elem.innerHTML = elem.dataset.originalContent;
  }

  return Promise.resolve();
}

export function querySelectorLast(selectors) {
  const elements = document.querySelectorAll(selectors);
  return elements.length ? elements[elements.length - 1] : false;
}

export function renderBefore(elem) {
  const data = getDataAll(elem);
  render(
    elem.dataset.target,
    data,
    function (...htmlElements) {
      this.before(htmlElements[0]);
    },
    elem.parentNode
  );
}

/**
 * Функция для проверки и установки фокуса на первое пустое поле
 */
export function setFocusOnEmptyField(form) {
  const inputs = Array.from(form.querySelectorAll('input, textarea')) // Выбрать все input и textarea
    .filter((field) => field.type !== 'hidden' && field.type !== 'submit' && field.type !== 'button'); // Исключить скрытые и кнопки

  for (const input of inputs) {
    if (!input.value) {
      // Проверяем первое пустое поле
      input.focus();
      return;
    }
  }
}

/**
 * Загружает данные с сервера по указанному URL и обновляет содержимое элемента на странице.
 *
 * @param {HTMLElement} elem Элемент, содержимое которого необходимо обновить данными с сервера.
 * @param {string} link URL для запроса данных.
 * @param {string} param Имя параметра из ответа сервера, значение которого будет отображено в elem.
 */
export function doLoader(elem, link, param) {
  let xmlhttp = new XMLHttpRequest();
  xmlhttp.onreadystatechange = function () {
    if (this.readyState == 4 && this.status == 200) {
      let answer = JSON.parse(this.responseText);
      elem.innerHTML = answer[param];
    }
  };
  xmlhttp.open('GET', link, true);
  xmlhttp.send();
}

export function createLoader(elm) {
  document.body.style.cursor = 'wait';

  let resolveContainer;

  const mask = document.createElement('div');

  new Promise((resolve, reject) => {
    resolveContainer = resolve;
    mask.classList.add('modalMask');
    document.body.append(mask);
  }).then((result) => {
    mask.remove();
    document.body.style.cursor = 'auto';
  });

  return resolveContainer;
}

export function resetFieldset(button, selector = 'fieldset') {
  const fieldsets = button.closest('form').querySelectorAll(selector);
  fieldsets.forEach((fieldset) => {
    const tracker = fieldset.fieldsetTracker;
    if (tracker) {
      tracker.restFildSet(fieldset); // Вызываем метод трекера
    } else {
      console.error('FieldsetManager is not initialized for this fieldset.');
    }
  });
}

export function setGoodSize(nodeList, maxHeight) {
  if (typeof nodeList == 'undefined' || nodeList === null) return;
  // debugger
  // Проверяем, является ли nodeList одиночным элементом
  if (nodeList.constructor.name !== 'NodeList' && nodeList.constructor.name !== 'Array') {
    nodeList = [nodeList];
  }

  let totalHeight = 0;
  let fontSizeCoefficient = 1;
  let lineHeightCoefficient = 1;

  nodeList.forEach(function (el) {
    totalHeight += el.offsetHeight;
  });

  while (totalHeight > maxHeight) {
    fontSizeCoefficient -= 0.01;
    lineHeightCoefficient -= 0.02;
    nodeList.forEach(function (el) {
      const newSize = parseInt(window.getComputedStyle(el).fontSize) * fontSizeCoefficient;
      if (newSize < 4) throw `Указан некоректный maxHeight ${maxHeight} текущий totalHeight ${totalHeight} учтите оступы`;
      el.style.lineHeight = parseInt(window.getComputedStyle(el).lineHeight) * lineHeightCoefficient + 'px';
      el.style.fontSize = parseInt(window.getComputedStyle(el).fontSize) * fontSizeCoefficient + 'px';
    });
    totalHeight = 0;
    nodeList.forEach(function (el) {
      totalHeight += el.offsetHeight;
    });
  }
}

/**
 * Вставляет HTML-контент в родительский элемент и выполняет встроенные скрипты
 * на том же месте, откуда они были вырезаны.
 *
 * @param {Object} options - Опции вставки.
 * @param {string} options.html - HTML-контент для вставки.
 * @param {HTMLElement} [options.parent] - Родительский элемент, куда будет вставлен контент.
 * @param {boolean|HTMLElement} [options.insertBefore] - Если true, вставлять в начало родителя;
 *                                                      если DOM-элемент — вставлять перед ним.
 * @param {boolean} [options.replaceParent=false] - Заменить содержимое родительского элемента.
 * @param {string} [options.selector=null] - Селектор для выбора вложенного элемента внутри HTML.
 * @param {string} [options.insertPosition=null] - Альтернативный вариант указания позиции вставки:
 *                                                 'start' — в начало, 'end' — в конец.
 * @returns {HTMLElement|null} - Родительский элемент после вставки или null при неудаче.
 */
export function insertHtmlWithScripts({
  html,
  parent,
  insertBefore,
  replaceParent = false,
  selector = null,
  insertPosition = null,
  noScripts = false
}) {
  // Если указан insertPosition, используем его для определения insertBefore.
  if (insertPosition) {
    if (insertPosition === 'start') {
      insertBefore = true;
    } else if (insertPosition === 'end') {
      insertBefore = false;
    } else {
      console.warn(`Неподдерживаемое значение insertPosition: "${insertPosition}". Используется значение по умолчанию.`);
    }
  }

  if (typeof html === 'string' && noScripts) {
    html = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }

  // Создаем элемент <template> для парсинга HTML
  const tempTemplate = document.createElement('template');
  if (typeof html !== 'string') {
    tempTemplate.content.append(html);
  } else
    tempTemplate.innerHTML = html.trim();

  // Проверка наличия родителя (parent) или возможности вставки через insertBefore
  const canUseParent = parent instanceof HTMLElement;
  const canUseInsertBefore = insertBefore instanceof HTMLElement;

  // Если мы хотим полностью очистить родительский элемент и parent валиден
  if (replaceParent && canUseParent) {
    while (parent.firstChild) {
      parent.removeChild(parent.firstChild);
    }
  }

  // Ищем все скрипты внутри содержимого <template>
  const scripts = tempTemplate.content.querySelectorAll('script');

  // Создаем метки (комментарии) на месте скриптов
  const placeholders = [];
  scripts.forEach((script) => {
    const placeholder = document.createComment('SCRIPT_PLACEHOLDER');
    script.parentNode.replaceChild(placeholder, script);
    placeholders.push({ placeholder, script });
  });

  // Определяем контейнер для переноса контента
  const parentContainer = selector ? tempTemplate.content.querySelector(selector) : tempTemplate.content;
  if (!parentContainer) {
    console.warn(`Элемент с селектором "${selector}" не найден.`);
    return parent || null;
  }

  // Переносим оставшийся контент из <template> в DocumentFragment
  const fragment = document.createDocumentFragment();
  while (parentContainer.firstChild) {
    fragment.appendChild(parentContainer.firstChild);
  }

  // Логика определения узла, перед которым вставляем
  let refNode = null;
  if (insertBefore) {
    // Если true — вставляем в начало (если есть валидный parent)
    if (insertBefore === true && canUseParent) {
      refNode = parent.firstChild;
    }
    // Если DOM-элемент
    else if (canUseInsertBefore) {
      refNode = insertBefore;
    }
  }

  // Восстанавливаем скрипты
  placeholders.forEach(({ placeholder, script: originalScript }) => {
    const newScript = document.createElement('script');

    // Копируем атрибуты
    Array.from(originalScript.attributes).forEach((attr) => {
      newScript.setAttribute(attr.name, attr.value);
    });

    // Если есть src — подключаем внешний скрипт
    if (originalScript.src) {
      newScript.src = originalScript.src;
      newScript.async = originalScript.async;
      newScript.defer = originalScript.defer;
    } else {
      // Иначе копируем текст скрипта
      newScript.textContent = originalScript.textContent;
    }

    placeholder.parentNode.replaceChild(newScript, placeholder);
  });

  /*
   * Вставка:
   * 1. Если есть валидный parent и refNode — используем parent.insertBefore.
   * 2. Если есть валидный parent без refNode — используем parent.appendChild.
   * 3. Если parent не валиден, но есть insertBefore (DOM-элемент),
   *    используем его parentNode для вставки.
   * 4. Если ни parent, ни insertBefore не подходят — выходим.
   */
  if (canUseParent && refNode) {
    parent.insertBefore(fragment, refNode);
  } else if (canUseParent) {
    parent.appendChild(fragment);
  } else if (!canUseParent && canUseInsertBefore) {
    const refParent = insertBefore.parentNode;
    if (!refParent) {
      console.warn('У insertBefore нет родительского элемента. Вставка невозможна.');
      return null;
    }
    refParent.insertBefore(fragment, refNode || insertBefore);
    parent = refParent;
  } else {
    console.warn('Невозможно выполнить вставку: не задан ни валидный parent, ни DOM-элемент insertBefore.');
    return null;
  }

  return parent;
}

export function previewImage(input) {
  const file = input.files[0];
  const reader = new FileReader();
  const imgContainer = input.closest('div').querySelector('img');
  if (imgContainer === null) return;
  const lastImage = imgContainer.src;

  // Путь к гифке загрузки
  const loadingGif = 'assets/img/loading-loading-forever.gif';

  // Устанавливаем гифку загрузки сразу после выбора файла
  if (file) {
    imgContainer.src = loadingGif;
    reader.onloadend = function () {
      imgContainer.src = reader.result;
    };
    reader.readAsDataURL(file); // Читаем файл как Data URL
  } else {
    // Возвращаем изначальное изображение, если файл не был выбран
    imgContainer.src = lastImage;
  }
}

export function showInputError({
  input,
  position = {
    top: null,
    left: null
  },
  message = ''
}) {
  // Находим или создаем контейнер для ошибки
  const container = input.parentNode;

  // Проверяем, существует ли сообщение об ошибке
  let errorElement = container.querySelector('.error-message');
  if (!errorElement) {
    // Создаем сообщение, если его нет
    errorElement = document.createElement('div');
    errorElement.className = 'error-message';
    container.insertBefore(errorElement, input); // Вставляем сообщение перед инпутом
  }

  requestAnimationFrame(() =>
    document.addEventListener('click', () => {
      errorElement.remove();
      input.classList.toggle('invalid', true);
    })
  );

  // Устанавливаем текст сообщения
  errorElement.textContent = message;

  // Устанавливаем позицию сообщения, если передана
  if (errorElement.style) {
    container.style.position = 'relative';
    errorElement.style.position = 'absolute';
    const inputRect = input.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    errorElement.style.top = position.top || `${inputRect.top - containerRect.top + errorElement.offsetHeight / 2 - 6}px`;
    errorElement.style.left = position.left || `${inputRect.left - containerRect.left}px`;
  }

  // Добавляем класс для стилизации инпута
  input.classList.toggle('invalid', true);
}
